import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  Sheet,
  Input,
  Card,
  Button,
  Grid,
  FormControl,
  FormLabel,
  Alert,
  Select,
  Option,
  CircularProgress
} from '@mui/joy';
import { FaSearch, FaFilter, FaSync } from 'react-icons/fa';
import { directSupabase } from '../lib/supabaseClient';
import { IconWrapper } from './IconWrapper';

const PAGE_SIZE = 20;

interface EventRow {
  id: string;
  event_type: string;
  event_date: string;
  project_id: string;
  project_name: string;
  customer_name: string;
}

const AllEventsManager: React.FC = () => {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    eventType: '',
    projectName: '',
    customerName: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [eventTypes, setEventTypes] = useState<string[]>([]);

  const fetchEventTypes = async () => {
    setEventTypes([]);
    try {
      const { data, error } = await directSupabase
        .from('project_events')
        .select('event_type');
      if (!error && data) {
        setEventTypes(Array.from(new Set(data.map((row: any) => row.event_type))).sort());
      }
    } catch (err) {
      setEventTypes([]);
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let scopedProjectIds: string[] | null = null;

      if (filters.projectName || filters.customerName) {
        let projectsQuery = directSupabase
          .from('projects')
          .select('id');

        if (filters.projectName) {
          projectsQuery = projectsQuery.ilike('project_name', `%${filters.projectName}%`);
        }
        if (filters.customerName) {
          projectsQuery = projectsQuery.ilike('customer_name', `%${filters.customerName}%`);
        }

        const { data: matchingProjects, error: projectsError } = await projectsQuery;
        if (projectsError) {
          throw projectsError;
        }

        scopedProjectIds = (matchingProjects || []).map((project: any) => project.id).filter(Boolean);

        if (scopedProjectIds.length === 0) {
          setEvents([]);
          setTotalCount(0);
          setLoading(false);
          return;
        }
      }

      let query = directSupabase
        .from('project_events')
        .select(`id, event_type, event_date, project_id, project:project_id (project_name, customer_name)`, { count: 'exact' })
        .order('event_date', { ascending: false });

      if (filters.eventType) {
        query = query.eq('event_type', filters.eventType);
      }
      if (scopedProjectIds) {
        query = query.in('project_id', scopedProjectIds);
      }

      const start = (page - 1) * PAGE_SIZE;
      const end = start + PAGE_SIZE - 1;
      query = query.range(start, end);

      const { data, error, count } = await query;
      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }

      const rows: EventRow[] = (data || []).map((row: any) => ({
        id: row.id,
        event_type: row.event_type,
        event_date: row.event_date,
        project_id: row.project_id,
        project_name: row.project?.project_name || '',
        customer_name: row.project?.customer_name || '',
      }));

      setTotalCount(count || 0);
      setEvents(rows);
      setLoading(false);
    } catch (err) {
      setError('Failed to load events');
      setEvents([]);
      setTotalCount(0);
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    fetchEventTypes();
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clearFilters = () => {
    setFilters({ eventType: '', projectName: '', customerName: '' });
    setPage(1);
  };

  const count = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  let pagination = null;
  if (count > 1) {
    pagination = (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
        <Button
          size="sm"
          variant="outlined"
          disabled={page <= 1}
          onClick={() => setPage(page - 1)}
        >
          Previous
        </Button>
        <Typography level="body-sm">
          Page {page} of {count}
        </Typography>
        <Button
          size="sm"
          variant="outlined"
          disabled={page >= count}
          onClick={() => setPage(page + 1)}
        >
          Next
        </Button>
      </Box>
    );
  }

  if (loading && !events.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography level="h3" mb={2}>All Project Events</Typography>
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button
            variant="outlined"
            startDecorator={<IconWrapper icon={FaFilter} />}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Button
            variant="plain"
            startDecorator={<IconWrapper icon={FaSync} />}
            onClick={loadData}
          >
            Refresh
          </Button>
        </Box>
        {showFilters && (
          <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
            <Grid container spacing={2}>
              <Grid xs={12} sm={6} md={3}>
                <FormControl>
                  <FormLabel>Event Type</FormLabel>
                  <Select
                    value={filters.eventType}
                    onChange={(_, value) => setFilters(f => ({ ...f, eventType: value || '' }))}
                  >
                    <Option value="">All Types</Option>
                    {eventTypes.map(type => (
                      <Option key={type} value={type}>{type}</Option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <FormControl>
                  <FormLabel>Project Name</FormLabel>
                  <Input
                    value={filters.projectName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters(f => ({ ...f, projectName: e.target.value }))}
                    placeholder="Search project..."
                    startDecorator={<IconWrapper icon={FaSearch} />}
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <FormControl>
                  <FormLabel>Customer Name</FormLabel>
                  <Input
                    value={filters.customerName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters(f => ({ ...f, customerName: e.target.value }))}
                    placeholder="Search customer..."
                    startDecorator={<IconWrapper icon={FaSearch} />}
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <Button
                  variant="plain"
                  color="neutral"
                  onClick={clearFilters}
                  sx={{ mt: 4 }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </Sheet>
        )}
      </Card>
      {/* Events Table */}
      <Card>
        <Table>
          <thead>
            <tr>
              <th>Event Date</th>
              <th>Event Type</th>
              <th>Project ID</th>
              <th>Project Name</th>
              <th>Customer Name</th>
            </tr>
          </thead>
          <tbody>
            {events.map(ev => (
              <tr key={ev.id}>
                <td>{ev.event_date ? new Date(ev.event_date).toLocaleString('en-GB', { timeZone: 'Europe/London' }) : ''}</td>
                <td>{ev.event_type}</td>
                <td>
                  <a href={`/projects/${ev.project_id}/workspace/implementation`} target="_blank" rel="noopener noreferrer">
                    {ev.project_id}
                  </a>
                </td>
                <td>
                  <a href={`/projects/${ev.project_id}/workspace/implementation`} target="_blank" rel="noopener noreferrer">
                    {ev.project_name}
                  </a>
                </td>
                <td>{ev.customer_name}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        {pagination}
      </Card>
    </Box>
  );
};

export default AllEventsManager; 